import { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Plus, Calendar as CalendarIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VideoPlanner = () => {
  const { t, language } = useLanguage();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title_en: '',
    title_ar: '',
    category: 'ppe',
    scheduled_date: '',
    status: 'idea',
    description_en: '',
    description_ar: ''
  });

  const categories = [
    { value: 'ppe', label_en: 'PPE Safety', label_ar: 'سلامة معدات الحماية' },
    { value: 'fire', label_en: 'Fire Safety', label_ar: 'سلامة مكافحة الحرائق' },
    { value: 'first-aid', label_en: 'First Aid', label_ar: 'الإسعافات الأولية' },
    { value: 'electrical', label_en: 'Electrical Safety', label_ar: 'السلامة الكهربائية' },
    { value: 'workplace', label_en: 'Workplace Safety', label_ar: 'سلامة مكان العمل' }
  ];

  const statuses = [
    { value: 'idea', label_en: 'Idea', label_ar: 'فكرة', color: '#A1A1AA' },
    { value: 'scripted', label_en: 'Scripted', label_ar: 'نص جاهز', color: '#FFB800' },
    { value: 'produced', label_en: 'Produced', label_ar: 'منتج', color: '#00E5FF' },
    { value: 'published', label_en: 'Published', label_ar: 'منشور', color: '#10B981' }
  ];

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      setError(null);
      const { data } = await axios.get(`${API}/videos`, { withCredentials: true });
      setVideos(data);
    } catch (error) {
      console.error('Failed to fetch videos:', error);
      setError(t('Failed to load videos. Please try again.', 'فشل تحميل الفيديوهات. يرجى المحاولة مرة أخرى.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/videos`, formData, { withCredentials: true });
      setIsDialogOpen(false);
      setFormData({
        title_en: '',
        title_ar: '',
        category: 'ppe',
        scheduled_date: '',
        status: 'idea',
        description_en: '',
        description_ar: ''
      });
      fetchVideos();
    } catch (error) {
      console.error('Failed to create video:', error);
    }
  };

  const getCategoryLabel = (cat) => {
    const found = categories.find(c => c.value === cat);
    return language === 'ar' ? found?.label_ar : found?.label_en;
  };

  const getStatusLabel = (status) => {
    const found = statuses.find(s => s.value === status);
    return language === 'ar' ? found?.label_ar : found?.label_en;
  };

  const getStatusColor = (status) => {
    return statuses.find(s => s.value === status)?.color || '#A1A1AA';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-3xl font-black tracking-tight text-[#EDEDED]">
            {t('Video Content Planner', 'مخطط محتوى الفيديو')}
          </h2>
          <p className="text-[#A1A1AA] mt-1">
            {t('Plan and schedule your daily safety videos', 'خطط وجدول مقاطع الفيديو اليومية الخاصة بك')}
          </p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-video-button" className="bg-safety-amber hover:bg-safety-amber/90 text-obsidian font-bold">
              <Plus className="w-4 h-4 me-2" />
              {t('New Video', 'فيديو جديد')}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-carbon border-[#27272A] text-[#EDEDED] max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-safety-amber">
                {t('Create New Video', 'إنشاء فيديو جديد')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('Title (English)', 'العنوان (إنجليزي)')}</Label>
                  <Input
                    data-testid="video-title-en"
                    value={formData.title_en}
                    onChange={(e) => setFormData({...formData, title_en: e.target.value})}
                    className="bg-obsidian border-[#27272A]"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('Title (Arabic)', 'العنوان (عربي)')}</Label>
                  <Input
                    data-testid="video-title-ar"
                    value={formData.title_ar}
                    onChange={(e) => setFormData({...formData, title_ar: e.target.value})}
                    className="bg-obsidian border-[#27272A]"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('Category', 'الفئة')}</Label>
                  <Select value={formData.category} onValueChange={(val) => setFormData({...formData, category: val})}>
                    <SelectTrigger data-testid="video-category" className="bg-obsidian border-[#27272A]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-carbon border-[#27272A]">
                      {categories.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {language === 'ar' ? cat.label_ar : cat.label_en}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t('Scheduled Date', 'التاريخ المحدد')}</Label>
                  <Input
                    data-testid="video-date"
                    type="date"
                    value={formData.scheduled_date}
                    onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                    className="bg-obsidian border-[#27272A]"
                    required
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end mt-6">
                <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)}>
                  {t('Cancel', 'إلغاء')}
                </Button>
                <Button data-testid="video-submit" type="submit" className="bg-safety-amber hover:bg-safety-amber/90 text-obsidian">
                  {t('Create', 'إنشاء')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center text-[#A1A1AA] py-12">{t('Loading...', 'تحميل...')}</div>
      ) : error ? (
        <div className="bg-signal-red/10 border border-signal-red/30 rounded-lg p-6 text-center">
          <p className="text-signal-red">{error}</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {videos.length === 0 ? (
            <div className="text-center py-12 bg-carbon border border-[#27272A] rounded-lg">
              <CalendarIcon className="w-12 h-12 text-[#52525B] mx-auto mb-3" />
              <p className="text-[#A1A1AA]">{t('No videos planned yet', 'لم يتم تخطيط أي فيديوهات بعد')}</p>
            </div>
          ) : (
            videos.map(video => (
              <div
                key={video.id}
                data-testid={`video-card-${video.id}`}
                className="bg-carbon border border-[#27272A] rounded-lg p-6 hover:border-safety-amber/30 transition-all"
                style={{ borderLeft: `4px solid ${getStatusColor(video.status)}` }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-bold text-[#EDEDED] mb-1">
                      {language === 'ar' ? video.title_ar : video.title_en}
                    </h3>
                    <div className="flex gap-3 text-sm text-[#A1A1AA]">
                      <span>{getCategoryLabel(video.category)}</span>
                      <span>•</span>
                      <span>{video.scheduled_date}</span>
                    </div>
                  </div>
                  <div
                    className="px-3 py-1 rounded-full text-xs font-semibold"
                    style={{ backgroundColor: `${getStatusColor(video.status)}20`, color: getStatusColor(video.status) }}
                  >
                    {getStatusLabel(video.status)}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default VideoPlanner;
